import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const hasMany = images.length > 1;

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => { setIndex(startIndex); }, [startIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, prev, next]);

  if (!images.length) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-[#1C1C1C]/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      style={{ animation: "tt-fade-up 300ms ease both" }}
    >
      <button
        data-testid="lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close image viewer"
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-[#FAF9F6] border border-[#FAF9F6]/30 hover:border-[#FAF9F6] hover:bg-[#FAF9F6]/10 transition-colors duration-300"
      >
        <X size={18} strokeWidth={1.25} />
      </button>

      {hasMany && (
        <div className="absolute top-8 left-8 overline text-[#FAF9F6]/70">
          {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
        </div>
      )}

      {hasMany && (
        <button
          data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous"
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-[#FAF9F6] border border-[#FAF9F6]/30 hover:border-[#FAF9F6] hover:bg-[#FAF9F6]/10 transition-colors duration-300 z-10"
        >
          <ChevronLeft size={22} strokeWidth={1.25} />
        </button>
      )}

      <div
        className="max-w-[92vw] max-h-[82vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={images[index]}
          src={images[index]}
          alt={`View ${index + 1}`}
          data-testid={`lightbox-image-${index}`}
          className="max-w-full max-h-[82vh] object-contain"
        />
      </div>

      {hasMany && (
        <button
          data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next"
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-[#FAF9F6] border border-[#FAF9F6]/30 hover:border-[#FAF9F6] hover:bg-[#FAF9F6]/10 transition-colors duration-300 z-10"
        >
          <ChevronRight size={22} strokeWidth={1.25} />
        </button>
      )}

      {hasMany && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((src, i) => (
            <button
              key={src}
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => setIndex(i)}
              className={`w-16 h-16 overflow-hidden border transition-colors duration-300 ${i === index ? "border-[#FAF9F6]" : "border-[#FAF9F6]/30 hover:border-[#FAF9F6]/70"}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
